<script setup lang="ts">
import type { FollowButtonProps } from "~/types/FollowButton";

const {
  isFollowedByUser,
  size = "small",
  id,
  name,
  type,
} = defineProps<FollowButtonProps>();
const featureToggles = useFeatureToggles();

const isIconActive = ref(false);
watch(
  () => isFollowedByUser,
  (following) => {
    isIconActive.value = following;
  },
  { immediate: true },
);

const activeClass = computed(
  () => `follow-icon-button-${isIconActive.value ? "active" : "in-active"}`,
);

const emit = defineEmits<{
  (e: "click"): void;
}>();

const { isLoggedIn } = useAuth();
const onFollowAuthCheck = useFollowAuthCheck();

const onClick = () => {
  onFollowAuthCheck(
    type === FollowFeatureType.Category
      ? {
          followType: FollowFeatureType.Category,
          id,
          name,
          followSource: "categoryListBlock",
          source: "FOLLOWING",
        }
      : {
          followType: type,
          id,
          name,
          followSource: "easy",
          source: "FOLLOWING",
        },
  );
  if (!isLoggedIn.value) return;

  isIconActive.value = !isIconActive.value; // for optimistic update
  emit("click");
};
</script>

<template>
  <button
    v-if="featureToggles.customerPreferences"
    class="rounded-full flex items-center justify-center transition-all z-[9]"
    :aria-label="`Follow button for ${name}`"
    :data-cy="`follow-icon-button-${id}${isIconActive ? '-active' : ''}`"
    :class="[
      variant === 'border' && 'border',
      size === 'small' ? 'w-8 h-8' : 'w-11 h-11',
    ]"
    @click="onClick"
  >
    <div
      class="text-platform-text-1 w-full h-full flex items-center justify-center"
      :class="[activeClass]"
    >
      <BaseIcon
        class="absolute"
        name="heart"
        :size="18"
      />
      <Transition>
        <BaseIcon
          v-if="isIconActive && isLoggedIn"
          data-test="follow-icon-button-following"
          class="absolute z-10"
          name="filledHeart"
          :size="18"
        />
      </Transition>
    </div>
  </button>
</template>

<style lang="postcss" scoped>
.follow-icon-button-active {
  animation-name: buttonLiked;
  animation-duration: 0.5s;
}

@keyframes buttonLiked {
  0% {
    transform: scale(1);
  }

  25% {
    transform: scale(1.15);
  }

  75% {
    transform: scale(1.15);
  }

  100% {
    transform: scale(1);
  }
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.25s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
